import { ExplanationDetails, Question, QuestionOptionData } from '../types/preptypes';
import { shuffleArray } from './shuffle';

type RawOption = QuestionOptionData | string | Record<string, unknown>;

/** API/DB may return options as a JSON string instead of a parsed array. */
export function parseQuestionOptions(
    options: QuestionOptionData[] | string | undefined | null
): RawOption[] {
    if (!options) return [];
    if (Array.isArray(options)) return options as RawOption[];
    if (typeof options === 'string') {
        try {
            const parsed = JSON.parse(options);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

// ── Embedded option labels ("A) text") ───────────────────────────────────────
// Some imported records bake the letter into the option text. The UI already renders its own
// letter, which produced doubled labels ("B  B) text") (QA CP-001). The data is being repaired
// at the source; this keeps already-loaded or not-yet-migrated records from showing them.

const LABEL_PREFIX = /^\s*\(?([A-Fa-f])[.):]\s+/;
const LABEL_LINE_PREFIX = /(^|\n)[ \t]*\(?[A-Fa-f][.):][ \t]+/g;

export function stripOptionLabel(text: string): string {
    return text.replace(LABEL_PREFIX, '');
}

/** True only when EVERY option starts with the next letter in sequence (A, B, C ...). */
export function hasEmbeddedLabels(texts: string[]): boolean {
    if (texts.length < 2) return false;
    return texts.every((text, i) => {
        const match = LABEL_PREFIX.exec(text);
        return !!match && match[1].toUpperCase() === String.fromCharCode(65 + i);
    });
}

function parseCorrectAnswerTexts(question: Question): Set<string> {
    const texts = new Set<string>();

    const addText = (value: unknown) => {
        if (value == null) return;
        const text = String(value).trim();
        if (!text) return;
        texts.add(text);
        texts.add(stripOptionLabel(text));
    };

    let correctAnswers: unknown = question.correct_answers;
    if (typeof correctAnswers === 'string') {
        try {
            correctAnswers = JSON.parse(correctAnswers);
        } catch {
            correctAnswers = correctAnswers.split(',').map((part) => part.trim());
        }
    }
    if (Array.isArray(correctAnswers)) {
        correctAnswers.forEach(addText);
    }

    if (question.correct_answer) {
        question.correct_answer.split(',').forEach(addText);
    }

    return texts;
}

function hydrateOption(option: RawOption, correctTexts: Set<string>): QuestionOptionData {
    if (typeof option === 'string') {
        const text = option.trim();
        return { text, isCorrect: correctTexts.has(text) };
    }

    const text = String(option.text ?? '').trim();
    const record = option as Record<string, unknown>;
    const explicitCorrect =
        record.isCorrect === true ||
        record.is_correct === true ||
        record.is_correct === 'true' ||
        record.isCorrect === 'true';

    return {
        text,
        isCorrect: explicitCorrect || (text.length > 0 && correctTexts.has(text)),
    };
}

// ── Explanations ─────────────────────────────────────────────────────────────

function asText(value: unknown): string {
    if (value == null) return '';
    if (Array.isArray(value)) return value.map(asText).filter(Boolean).join('\n');
    return String(value).trim();
}

/**
 * The generator stores explanations as { why_correct, why_wrong_A, why_wrong_C, ... } while the
 * explanation card renders { summary, breakdown, otherOptions }. Convert the former so those
 * explanations are not blank (QA CP-017). Letters refer to the stored option order, so they are
 * translated to option text here, before the options get shuffled.
 */
export function normalizeExplanationDetails(
    raw: unknown,
    options: QuestionOptionData[],
    stripLabels: boolean
): ExplanationDetails | undefined {
    let details: Record<string, unknown> | null = null;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') details = parsed as Record<string, unknown>;
        } catch {
            details = null;
        }
    } else if (raw && typeof raw === 'object') {
        details = raw as Record<string, unknown>;
    }
    if (!details) return undefined;

    const result: ExplanationDetails = { ...details };

    if (typeof result.otherOptions === 'string' && stripLabels) {
        result.otherOptions = result.otherOptions.replace(LABEL_LINE_PREFIX, '$1');
    }
    if (Array.isArray(result.breakdown) && stripLabels) {
        result.breakdown = result.breakdown.map((line) => String(line).replace(LABEL_PREFIX, ''));
    }

    const wrongByLetter: Array<[string, string]> = [];
    Object.keys(details).forEach((key) => {
        const match = /^why_wrong_([A-Fa-f])$/.exec(key);
        if (match) wrongByLetter.push([match[1].toUpperCase(), asText(details![key])]);
    });
    const whyWrong = details.why_wrong;
    if (whyWrong && typeof whyWrong === 'object' && !Array.isArray(whyWrong)) {
        Object.entries(whyWrong as Record<string, unknown>).forEach(([key, value]) => {
            if (/^[A-Fa-f]$/.test(key)) wrongByLetter.push([key.toUpperCase(), asText(value)]);
        });
    }

    if (!result.otherOptions) {
        if (wrongByLetter.length > 0) {
            result.otherOptions = wrongByLetter
                .sort((a, b) => a[0].localeCompare(b[0]))
                .filter(([, reason]) => reason)
                .map(([letter, reason]) => {
                    const option = options[letter.charCodeAt(0) - 65];
                    return option ? `${option.text} — ${reason}` : reason;
                })
                .join('\n');
        } else if (typeof whyWrong === 'string' || Array.isArray(whyWrong)) {
            result.otherOptions = asText(whyWrong);
        } else if (details.why_other_options_are_wrong) {
            result.otherOptions = asText(details.why_other_options_are_wrong);
        }
    }

    if (!result.breakdown && details.why_correct) {
        result.breakdown = [asText(details.why_correct)];
    }
    if (!result.summary && (details.why_correct || result.otherOptions)) {
        result.summary = 'Why this answer is correct';
    }

    return result;
}

const SKILL_LEVELS = new Set(['Beginner', 'Intermediate', 'Advanced', 'Expert']);
const BLOOM_LEVELS = new Set(['Knowledge', 'Comprehension', 'Application', 'Analysis', 'Synthesis', 'Evaluation']);

/**
 * AWS rows historically stored Bloom labels in `difficulty` while CompTIA has no difficulty
 * column at all. Prefer a real skill level so filters and badges stay consistent.
 */
export function resolveDifficulty(question: Question): string {
    const skill = question.skill_level?.trim();
    if (skill && SKILL_LEVELS.has(skill)) return skill;

    const difficulty = question.difficulty?.trim();
    if (difficulty && SKILL_LEVELS.has(difficulty)) return difficulty;
    if (difficulty && BLOOM_LEVELS.has(difficulty)) return 'Intermediate';

    return difficulty || 'Intermediate';
}

export function normalizeQuestion(question: Question): Question {
    const correctTexts = parseCorrectAnswerTexts(question);
    const hydrated = parseQuestionOptions(question.options as QuestionOptionData[] | string)
        .map((option) => hydrateOption(option, correctTexts))
        .filter((option) => option.text.length > 0);

    const stripLabels = hasEmbeddedLabels(hydrated.map((option) => option.text));
    const options = stripLabels
        ? hydrated.map((option) => ({ ...option, text: stripOptionLabel(option.text) }))
        : hydrated;

    const difficulty = resolveDifficulty(question);
    const cognitive =
        question.cognitive_level?.trim() ||
        (BLOOM_LEVELS.has(question.difficulty?.trim() ?? '') ? question.difficulty!.trim() : question.cognitive_level);

    return {
        ...question,
        difficulty,
        skill_level: question.skill_level?.trim() || (SKILL_LEVELS.has(difficulty) ? difficulty : question.skill_level),
        cognitive_level: cognitive ?? null,
        options,
        explanation_details: normalizeExplanationDetails(question.explanation_details, options, stripLabels),
    };
}

// ── Shuffling without breaking letter references ─────────────────────────────

const letterOf = (index: number) => String.fromCharCode(65 + index);

/**
 * Explanations sometimes say "Option C and D are incorrect". Options are shuffled when a quiz
 * starts, so those letters must follow the options to their new positions (QA CP-023).
 */
export function remapOptionLetters(text: string, letterMap: Record<string, string>): string {
    if (!text) return text;
    const swap = (letters: string) => letters.replace(/[A-F]/g, (l) => letterMap[l] ?? l);

    // One pass over three alternatives so a letter is never remapped twice:
    //  1. "Option C", "Options C and D", "Answer (B)"   2. a bare "(C)"   3. a list line "C) ..."
    const pattern =
        /\b(Options?|Answers?|Choices?)(\s+)((?:\(?[A-F]\)?)(?:\s*(?:,|and|&|or|\/)\s*\(?[A-F]\)?)*)(?![A-Za-z])|\(([A-F])\)|(^|\n)([ \t]*)([A-F])([.):][ \t])/g;

    return text.replace(
        pattern,
        (
            match: string,
            noun?: string,
            space?: string,
            letters?: string,
            parenLetter?: string,
            lineStart?: string,
            indent?: string,
            lineLetter?: string,
            lineRest?: string
        ) => {
            if (noun !== undefined) return `${noun}${space}${swap(letters ?? '')}`;
            if (parenLetter !== undefined) return `(${letterMap[parenLetter] ?? parenLetter})`;
            if (lineLetter !== undefined) return `${lineStart}${indent}${letterMap[lineLetter] ?? lineLetter}${lineRest}`;
            return match;
        }
    );
}

function remapDeep(value: unknown, letterMap: Record<string, string>): unknown {
    if (typeof value === 'string') return remapOptionLetters(value, letterMap);
    if (Array.isArray(value)) return value.map((item) => remapDeep(item, letterMap));
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, remapDeep(item, letterMap)])
        );
    }
    return value;
}

/** Shuffle a normalized question's options and keep any letter references in step. */
export function shuffleQuestionOptions(question: Question): Question {
    const shuffled = shuffleArray(question.options.map((option, originalIndex) => ({ option, originalIndex })));
    const letterMap: Record<string, string> = {};
    shuffled.forEach(({ originalIndex }, newIndex) => {
        letterMap[letterOf(originalIndex)] = letterOf(newIndex);
    });

    return {
        ...question,
        options: shuffled.map(({ option }) => option),
        explanation: remapOptionLetters(question.explanation, letterMap),
        explanation_details: question.explanation_details
            ? (remapDeep(question.explanation_details, letterMap) as ExplanationDetails)
            : question.explanation_details,
    };
}
